import { CUKCUK_MIN_MATCH_SCORE } from '../constants/cukcuk';
import type { MenuOption } from '../types/cukcukImport';
import { buildCukcukOptionCode } from './cukcukParser';

const PREFIX_PATTERNS: Array<{ pattern: RegExp; canonical: string }> = [
  { pattern: /^đổi\s*hạt\s*:\s*/i, canonical: 'doi hat: ' },
  { pattern: /^thêm\s*shot\s*:\s*/i, canonical: 'them shot: ' },
  { pattern: /^doi\s*hat\s*:\s*/i, canonical: 'doi hat: ' },
  { pattern: /^them\s*shot\s*:\s*/i, canonical: 'them shot: ' },
];

const stripDiacritics = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd');

/**
 * Chuẩn hóa tên option để so khớp mờ:
 * - bỏ dấu, lowercase
 * - chuẩn hóa tiền tố "Đổi hạt", "Thêm shot"
 * - gom khoảng trắng và ký tự phân cách
 */
export const normalizeOptionName = (name: string): string => {
  let text = stripDiacritics(name.trim().toLowerCase());

  PREFIX_PATTERNS.forEach(({ pattern, canonical }) => {
    text = text.replace(pattern, canonical);
  });

  return text
    .replace(/[_–—]/g, '-')
    .replace(/\s*:\s*/g, ': ')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Phần nội dung sau tiền tố (vd: "ethiopia sidamo twakok g1") */
export const extractOptionPayload = (name: string): string => {
  const normalized = normalizeOptionName(name);
  const payload = normalized.replace(/^(doi hat|them shot):\s*/, '');
  return payload.trim();
};

const compact = (value: string): string => normalizeOptionName(value).replace(/[^a-z0-9]/g, '');

export type OptionMatchMethod =
  | 'exact'
  | 'code'
  | 'source'
  | 'normalized'
  | 'payload'
  | 'compact'
  | 'fuzzy'
  | 'none';

export interface OptionMatchResult {
  option: MenuOption | null;
  score: number;
  method: OptionMatchMethod;
}

const scoreOptionMatch = (
  optionName: string,
  option: MenuOption,
  parentDishCode?: string,
): { score: number; method: OptionMatchMethod } => {
  const query = optionName.trim();
  if (!query) return { score: 0, method: 'none' };

  const queryNormalized = normalizeOptionName(query);
  const candidateName = option.name.trim();
  const candidateNormalized = normalizeOptionName(candidateName);
  const queryPayload = extractOptionPayload(query);
  const candidatePayload = extractOptionPayload(candidateName);
  const queryCompact = compact(query);
  const candidateCompact = compact(candidateName);
  const queryCode = buildCukcukOptionCode(query);
  const contextualCode = parentDishCode ? buildCukcukOptionCode(query, parentDishCode) : undefined;

  if (candidateName === query) return { score: 1000, method: 'exact' };
  if (option.code === query || option.code === queryCode) return { score: 980, method: 'code' };
  if (contextualCode && option.code === contextualCode) return { score: 990, method: 'code' };
  if (option.source.cukcukName?.trim() === query) return { score: 960, method: 'source' };

  if (candidateNormalized === queryNormalized) return { score: 900, method: 'normalized' };

  if (queryPayload && candidatePayload && queryPayload === candidatePayload) {
    return { score: 860, method: 'payload' };
  }

  if (queryCompact && candidateCompact && queryCompact === candidateCompact) {
    return { score: 840, method: 'compact' };
  }

  if (candidateNormalized.includes(queryNormalized) || queryNormalized.includes(candidateNormalized)) {
    const lengthGap = Math.abs(candidateNormalized.length - queryNormalized.length);
    return { score: 700 - Math.min(lengthGap, 200), method: 'fuzzy' };
  }

  if (
    queryPayload &&
    candidatePayload &&
    (candidatePayload.includes(queryPayload) || queryPayload.includes(candidatePayload))
  ) {
    const lengthGap = Math.abs(candidatePayload.length - queryPayload.length);
    return { score: 650 - Math.min(lengthGap, 200), method: 'fuzzy' };
  }

  const sourceName = option.source.cukcukName?.trim();
  if (sourceName) {
    const sourceNormalized = normalizeOptionName(sourceName);
    if (sourceNormalized === queryNormalized) return { score: 820, method: 'source' };
    if (sourceNormalized.includes(queryNormalized) || queryNormalized.includes(sourceNormalized)) {
      return { score: 600, method: 'fuzzy' };
    }
  }

  return { score: 0, method: 'none' };
};

/** Indexed catalog for O(1) code lookup and fast name matching. */
export class OptionCatalogIndex {
  private readonly options: MenuOption[];
  private readonly byCode = new Map<string, MenuOption>();
  private readonly byNormalizedName = new Map<string, MenuOption[]>();

  constructor(options: MenuOption[]) {
    this.options = options;
    options.forEach((option) => {
      const code = option.code?.trim();
      if (code) this.byCode.set(code, option);

      const nameKey = normalizeOptionName(option.name);
      if (nameKey) {
        const bucket = this.byNormalizedName.get(nameKey) ?? [];
        bucket.push(option);
        this.byNormalizedName.set(nameKey, bucket);
      }
    });
  }

  get size(): number {
    return this.options.length;
  }

  getAll(): MenuOption[] {
    return this.options;
  }

  lookup(optionName: string, parentDishCode?: string): OptionMatchResult {
    const query = optionName.trim();
    if (!query || !this.options.length) {
      return { option: null, score: 0, method: 'none' };
    }

    const queryCode = buildCukcukOptionCode(query);
    const contextualCode = parentDishCode ? buildCukcukOptionCode(query, parentDishCode) : undefined;

    const directCandidates = new Set<MenuOption>();
    [query, queryCode, contextualCode].forEach((code) => {
      if (!code) return;
      const matched = this.byCode.get(code);
      if (matched) directCandidates.add(matched);
    });

    const normalizedKey = normalizeOptionName(query);
    (this.byNormalizedName.get(normalizedKey) ?? []).forEach((option) => directCandidates.add(option));

    let bestOption: MenuOption | null = null;
    let bestScore = 0;
    let bestMethod: OptionMatchMethod = 'none';

    const candidates = directCandidates.size ? Array.from(directCandidates) : this.options;

    candidates.forEach((option) => {
      const { score, method } = scoreOptionMatch(query, option, parentDishCode);
      if (score > bestScore) {
        bestScore = score;
        bestOption = option;
        bestMethod = method;
      }
    });

    if (bestScore < CUKCUK_MIN_MATCH_SCORE) {
      return { option: null, score: bestScore, method: 'none' };
    }

    return { option: bestOption, score: bestScore, method: bestMethod };
  }
}

/**
 * Map tên option từ CUKCUK (hóa đơn / STPV) sang catalog `MenuOption` hiện có.
 * Ưu tiên exact match, sau đó so khớp theo tên đã normalize.
 */
export const mapCukcukOption = (
  optionName: string,
  existingOptions: MenuOption[],
  parentDishCode?: string,
): MenuOption | null => {
  const index = existingOptions instanceof OptionCatalogIndex
    ? existingOptions
    : new OptionCatalogIndex(existingOptions);

  return index.lookup(optionName, parentDishCode).option;
};

export const mapCukcukOptionWithScore = (
  optionName: string,
  existingOptions: MenuOption[] | OptionCatalogIndex,
  parentDishCode?: string,
): OptionMatchResult => {
  const index = existingOptions instanceof OptionCatalogIndex
    ? existingOptions
    : new OptionCatalogIndex(existingOptions);

  return index.lookup(optionName, parentDishCode);
};